type SelectionState = Record<string, string | null>

declare global {
  interface Window {
    Shopify: any;
    jspdf: {
      jsPDF: any;
    };
    bundleTranslations: {
      select: string;
      adding: string;
      addToCart: string;
      addedToCart: string;
      nothingToAdd: string;
      unableToAdd: string;
      genericError: string;
      stepCounter: string;
      size: string;
      leftSole: string;
      rightSole: string;
      leftStrap: string;
      rightStrap: string;
      leftOutsole: string;
      rightOutsole: string;
      leftOrnaments: string;
      rightOrnaments: string;
      leftPin: string;
      rightPin: string;
      selectPin: string;
      pin: string;
      selected: string;
      none: string;
      selectOptionError: string;
      summary: string;
      pdfTitle: string;
      configurationLink: string;
      generatingPdf: string;
      summary: string;
      pdfTitle: string;
      configurationLink: string;
      generatingPdf: string;
    };
  }
}

(async () => {
  function uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
      (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
  }  

  const COLOR_STYLES: Record<string, string> = {
    'acid lime': '#c1d82f',
    'aspen green': '#6a8f6b',
    'aurorora red': '#d94d4d',
    'aurora red': '#d94d4d',
    black: '#1a1a1a',
    blue: '#1d7dd8',
    'royal blue': '#143b86',
    'vibrant yellow': '#ffcf3f',
    white: '#ffffff',
    'navy blue': '#0d2d5c',
    turq: '#33b3a6',
    'vanilla cream': '#f6e2c6',
    veronica: '#4a3b8f',
    'day break': '#a6a3d1',
    'fire cracker': '#ee6b45',
    'hot pink': '#ff4d88',
    'multi color': 'linear-gradient(135deg,#ff4d88 0%,#ffd93d 50%,#21c2c1 100%)',
  }

  const FALLBACK_SWATCH = 'linear-gradient(135deg,#f5f5f5 0%,#dedede 100%)'
  const PREVIEW_ORDER = [
    'size',
    'left-sole',
    'right-sole',
    'left-strap',
    'right-strap',
    'pin-1',
    'pin-2',
    'pin-3',
    'pin-4',
    'pin-5',
    'pin-6',
    'pin-7',
    'pin-8',
    'left-ornament',
    'right-ornament',
  ] as const

  let navigateToWizardStep: (index: number) => void = () => {}

  function initBundles() {
    document
      .querySelectorAll<HTMLElement>('[data-bundle-root]')
      .forEach((root) => initializeBundle(root))
  }

  function initializeBundle(root: HTMLElement) {
    if (root.dataset.bundleReady === 'true') {
      return
    }

    const currency = typeof window !== 'undefined' && window?.Shopify?.currency?.active ? window?.Shopify?.currency?.active : 'USD'

    const selections: SelectionState = {}

    root.querySelectorAll<HTMLElement>('[data-option-group]').forEach((section) => {
      const groupKey = section.dataset.optionGroup
      if (!groupKey) return
      const selectedId = section.dataset.selectedId || null
      selections[groupKey] = selectedId
    })

    // Check URL for config
    const urlParams = new URLSearchParams(window.location.search)
    const configParam = urlParams.get('config')
    if (configParam) {
      try {
        const decoded = atob(configParam)
        const parsed = JSON.parse(decoded)
        Object.assign(selections, parsed)
      } catch (e) {
        console.error('Failed to parse config from URL', e)
      }
    }

    const totalDisplay = root.querySelector<HTMLElement>('[data-bundle-total]')
    const addToCartButton = root.querySelector<HTMLButtonElement>('[data-bundle-add-to-cart]')
    const errorContainer = root.querySelector<HTMLElement>('[data-bundle-error]')
    const successContainer = root.querySelector<HTMLElement>('[data-bundle-success]')

    const formatter = createPriceFormatter(currency)
    let isSubmitting = false
    let activePinPosition: number | null = 1

    styleSwatches(root)
    updateAllSections(root, selections)
    updatePreview()
    updateTotal()
    updateSummary()
    //updateButtonState()
    updatePinDropdownStatuses()
    updatePinAvailability(root, selections)
    
    // Initialize wizard for mobile
    initWizard()

    // Preload images for smoother interaction
    setTimeout(() => preloadImages(root), 1000)

    // === SVG PART INTERACTIONS ===
    
    // Add click handlers for SVG parts (soles and straps)
    root.querySelectorAll<SVGRectElement>('[data-bundle-part]').forEach((svgPart) => {
      // Click handler - navigate to section
      svgPart.addEventListener('click', () => {
        const partName = svgPart.dataset.bundlePart
        if (!partName) return
        
        scrollToSection(partName)
      })
      
      // Hover effects
      svgPart.addEventListener('mouseenter', () => {
        const partName = svgPart.dataset.bundlePart
        if (!partName) return

        svgPart.setAttribute('aria-current', 'true')
      })
      
      svgPart.addEventListener('mouseleave', () => {
        svgPart.setAttribute('aria-current', 'false')
      })
    })
    
    // Set up Intersection Observer for desktop view-based highlighting
    if (window.innerWidth >= 1024) {
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -40% 0px',
        threshold: [0, 0.5, 1]
      }
      
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const section = entry.target as HTMLElement
            const partName = section.dataset.optionGroup
            
            if (partName && (partName.includes('sole') || partName.includes('strap'))) {
              highlightSvgPart(partName)
            }
            
            // Auto-select first available pin when pins section comes into view
            if (section.hasAttribute('data-pins-section') && activePinPosition === null) {
              let startPin = 1
              while (startPin <= 8 && isPinBlocked(root, selections, startPin)) {
                startPin++
              }
              if (startPin <= 8) {
                selectPinPosition(startPin)
              }
            }
          }
        })
      }, observerOptions)
      
      // Observe all sole and strap sections
      const sectionsToObserve = [
        root.querySelector('[data-option-group="left-sole"]'),
        root.querySelector('[data-option-group="right-sole"]'),
        root.querySelector('[data-option-group="left-strap"]'),
        root.querySelector('[data-option-group="right-strap"]'),
        root.querySelector('[data-pins-section]')
      ].filter(Boolean) as HTMLElement[]
      
      sectionsToObserve.forEach(section => sectionObserver.observe(section))
    }

    root.querySelectorAll<HTMLButtonElement>('[data-bundle-option]').forEach((input) => {
      input.addEventListener('change', () => {
        
        if (input.disabled || isSubmitting) return
        const section = input.closest<HTMLElement>('[data-option-group]')
        if (!section) return
        const groupKey = section.dataset.optionGroup
        const variantId = input.value
        // Allow empty variantId (for "None" selection)
        if (!groupKey) return
        
        // --- ORNAMENT CONFLICT CHECK ---
        if ((groupKey === 'left-ornament' || groupKey === 'right-ornament') && variantId) {
          try {
            const replacedPinsData = input.dataset.replacedPins
            const configData = input.dataset.config
            
            let replacedPins: number[] = []
            
            if (replacedPinsData) {
              try {
                const parsed = JSON.parse(replacedPinsData)
                if (Array.isArray(parsed)) {
                  replacedPins = parsed.map(Number)
                } else if (typeof parsed === 'string') {
                  // Handle comma-separated string if that's how it comes through
                  replacedPins = parsed.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n))
                } else if (typeof parsed === 'number') {
                  replacedPins = [parsed]
                }
              } catch (e) {
                console.warn('Failed to parse replacedPins JSON:', replacedPinsData)
              }
            } 
            
            // Fallback to config if no pins found yet
            if (replacedPins.length === 0 && configData) {
               try {
                 const config = JSON.parse(configData)
                 if (config.blockedPins && Array.isArray(config.blockedPins)) {
                   replacedPins = config.blockedPins.map(Number)
                 }
               } catch (e) {
                 console.warn('Failed to parse config JSON:', configData)
               }
            }
            
            console.log('[Bundle] Ornament selected:', groupKey, variantId)
            console.log('[Bundle] Replaced pins for ornament:', replacedPins)
            
            if (replacedPins.length > 0) {
              const conflicts: number[] = []
              replacedPins.forEach(pinNum => {
                const pinKey = `pin-${pinNum}`
                if (selections[pinKey]) {
                  conflicts.push(pinNum)
                }
              })
              
              console.log('[Bundle] Conflicts found:', conflicts)
              
              if (conflicts.length > 0) {
                 const pinNames = conflicts.map(num => {
                  const sideLabel = num < 5 ? window.bundleTranslations.leftPin : window.bundleTranslations.rightPin
                  const relativeNum = num < 5 ? num : num - 4
                  return sideLabel.replace('{{ number }}', String(relativeNum))
                })
                
                // Use setTimeout to let the UI update slightly before blocking with alert/confirm
                // This ensures the radio button visual state might update or at least we are out of the immediate event loop
                // However, we need to block the logic.
                
                const confirmed = window.confirm(
                  `Selecting this ornament will remove the following pins: ${pinNames.join(', ')}. Do you want to continue?`
                )
                
                if (!confirmed) {
                  // Revert selection
                  const previousId = selections[groupKey]
                  // We need to visually revert the radio button
                  // Find the radio button that matches previousId and check it
                  // If previousId is null/empty, check the 'None' option
                  
                  // Use setTimeout to ensure we override the browser's default radio behavior
                  setTimeout(() => {
                    if (previousId) {
                        const prevInput = section.querySelector<HTMLInputElement>(`input[value="${previousId}"]`)
                        if (prevInput) {
                            prevInput.checked = true
                            // Manually trigger change to ensure state consistency? 
                            // No, because selections[groupKey] wasn't updated yet.
                        }
                    } else {
                        const noneInput = section.querySelector<HTMLInputElement>(`input[value=""]`)
                        if (noneInput) noneInput.checked = true
                    }
                  }, 0)
                  
                  return // Exit the handler, do not update selections[groupKey]
                }
                
                // User confirmed, clear conflicting pins
                conflicts.forEach(pinNum => {
                   selections[`pin-${pinNum}`] = null
                   const pinInput = root.querySelector<HTMLElement>(`[data-option-group="pin-${pinNum}"]`)
                   if (pinInput) pinInput.dataset.selectedId = ''
                })
              }
            }
          } catch (e) {
            console.error('Error checking ornament conflicts', e)
          }
        }
        // -----------------------------

        selections[groupKey] = variantId
        section.dataset.selectedId = variantId

        if (groupKey === 'size') {
          const size = input.dataset.label

          root.querySelectorAll<HTMLElement>('[data-size]').forEach(el => 
            el.parentElement?.setAttribute('aria-hidden', 'true')
          )
          root.querySelectorAll<HTMLElement>(`[data-size*="${size}"]`).forEach(el => 
            el.parentElement?.setAttribute('aria-hidden', 'false')
          )

          root.querySelectorAll<HTMLElement>(`[data-size]:checked`).forEach(el => {
            let matchingElement = el.parentElement?.parentElement?.querySelector<HTMLInputElement>(`[data-size*="${size}"][data-label="${el.dataset.label}"]`)
            console.log('matchingElement', matchingElement)
            if (!matchingElement) {
              matchingElement = el.parentElement?.parentElement?.querySelector<HTMLInputElement>(`[data-size*="${size}"]`)
              matchingElement?.click()
            }
            if (matchingElement) {
              matchingElement.checked = true
            }
            const parentSection = matchingElement?.parentElement?.parentElement?.parentElement?.parentElement
            if (parentSection) {
              parentSection.setAttribute('data-selected-id', matchingElement?.value || '')
              selections[parentSection.dataset.optionGroup || ''] = matchingElement?.value || ''
            }
          })
          
        }
        updateSectionState(section, groupKey, selections)
        updatePreview()
        updateTotal()
        updateSummary()
        //updateOrnamentWarnings(root, selections)
        updatePinAvailability(root, selections)
        //updateButtonState()
        hideMessage(errorContainer)
        hideMessage(successContainer)
      })
    })

    root.querySelectorAll<HTMLButtonElement>('[data-bundle-dropdown-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation()
        const section = toggle.closest<HTMLElement>('[data-option-group]')
        if (!section) return
        const content = section.querySelector<HTMLElement>('.grid')
        const icon = toggle.querySelector<SVGElement>('[data-bundle-dropdown-icon]')
        
        if (!content) return
        
        const isExpanded = content.style.display !== 'none'
        content.style.display = isExpanded ? 'none' : 'grid'
        
        if (icon) {
          icon.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'
        }
      })
    })

    // Handle pin dropdown toggle
    const pinDropdownToggle = root.querySelector<HTMLButtonElement>('[data-bundle-pin-dropdown-toggle]')
    const pinDropdownMenu = root.querySelector<HTMLElement>('[data-bundle-pin-dropdown-menu]')
    
    if (pinDropdownToggle && pinDropdownMenu) {
      pinDropdownToggle.addEventListener('click', (e) => {
        e.stopPropagation()
        const isOpen = !pinDropdownMenu.classList.contains('hidden')
        pinDropdownMenu.classList.toggle('hidden', isOpen)
        const icon = pinDropdownToggle.querySelector<SVGElement>('[data-bundle-pin-dropdown-icon]')
        if (icon) {
          icon.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)'
        }
      })

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!pinDropdownMenu.contains(e.target as Node) && !pinDropdownToggle.contains(e.target as Node)) {
          pinDropdownMenu.classList.add('hidden')
          const icon = pinDropdownToggle.querySelector<SVGElement>('[data-bundle-pin-dropdown-icon]')
          if (icon) {
            icon.style.transform = 'rotate(0deg)'
          }
        }
      })
    }

    // Helper to select a pin position
    function selectPinPosition(position: number) {
      if (!position) return
      
      // Check if pin is blocked by ornament
      if (isPinBlocked(root, selections, position)) return

      activePinPosition = position
      updateDropdownLabel(position)
      
      // Close dropdown if open
      if (pinDropdownMenu) {
        pinDropdownMenu.classList.add('hidden')
      }
      
      const icon = pinDropdownToggle?.querySelector<SVGElement>('[data-bundle-pin-dropdown-icon]')
      if (icon) {
        icon.style.transform = 'rotate(0deg)'
      }
      
      highlightPinCircle(position)
    }

    // Handle pin position selection from dropdown
    root.querySelectorAll<HTMLButtonElement>('[data-bundle-pin-position-select]').forEach((button) => {
      button.addEventListener('click', () => {
        const position = parseInt(button.dataset.bundlePinPositionSelect || '0', 10)
        selectPinPosition(position)
      })
    })

    // Handle SVG pin circle clicks
    root.querySelectorAll<SVGCircleElement>('[data-bundle-pin-circle]').forEach((circle) => {
      circle.addEventListener('click', () => {
        const position = parseInt(circle.dataset.bundlePinCircle || '0', 10)
        selectPinPosition(position)
        
        // Navigate to pins section on desktop or pins step on mobile
        const isMobile = window.innerWidth < 1024
        const pinsSection = root.querySelector<HTMLElement>('[data-pins-section]')
        
        if (isMobile && pinsSection) {
          // On mobile, navigate to the pins wizard step
          const stepNumber = pinsSection.dataset.wizardStep
          if (stepNumber) {
            navigateToWizardStep(parseInt(stepNumber, 10) - 1)
          }
        } else if (pinsSection) {
          // On desktop, smooth scroll to the pins section
          pinsSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    })

    // Handle pin variant selection
    root.querySelectorAll<HTMLButtonElement>('[data-bundle-pin-variant]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || activePinPosition === null) return
        
        const variantId = button.dataset.variantId
        if (!variantId) return
        
        const groupKey = `pin-${activePinPosition}`
        selections[groupKey] = variantId
        
        const hiddenInput = root.querySelector<HTMLInputElement>(
          `[data-option-group="${groupKey}"]`
        )
        if (hiddenInput) {
          hiddenInput.dataset.selectedId = variantId
        }
        
        updatePreview()
        updateTotal()
        updateSummary()
        //updateButtonState()
        updatePinDropdownStatuses()
        clearPinCircleHighlight()
        hideMessage(errorContainer)
        hideMessage(successContainer)
        
        // Auto-advance to next available pin position
        let nextPos = (activePinPosition || 0) + 1
        while (nextPos <= 8 && isPinBlocked(root, selections, nextPos)) {
          nextPos++
        }

        if (nextPos <= 8) {
          selectPinPosition(nextPos)
        } else {
          activePinPosition = null
          // Reset dropdown label
          const dropdownLabel = root.querySelector<HTMLElement>('[data-bundle-pin-dropdown-label]')
          if (dropdownLabel) {
             dropdownLabel.textContent = window.bundleTranslations.selectPin + ' 1'
          }
        }
      })
    })

    // Handle clear pin button
    root.querySelectorAll<HTMLButtonElement>('[data-bundle-pin-clear]').forEach((button) => {
      button.addEventListener('click', () => {
        if (activePinPosition === null) return
        
        const groupKey = `pin-${activePinPosition}`
        selections[groupKey] = null
        
        const hiddenInput = root.querySelector<HTMLInputElement>(
          `[data-option-group="${groupKey}"]`
        )
        if (hiddenInput) {
          hiddenInput.dataset.selectedId = ''
        }
        
        updatePreview()
        updateTotal()
        updateSummary()
        updateButtonState()
        updatePinDropdownStatuses()
        clearPinCircleHighlight()
        hideMessage(errorContainer)
        hideMessage(successContainer)
        
        // Auto-advance to next available pin position
        let nextPos = (activePinPosition || 0) + 1
        while (nextPos <= 8 && isPinBlocked(root, selections, nextPos)) {
          nextPos++
        }

        if (nextPos <= 8) {
          selectPinPosition(nextPos)
        } else {
          activePinPosition = null
          // Reset dropdown label
          const dropdownLabel = root.querySelector<HTMLElement>('[data-bundle-pin-dropdown-label]')
          if (dropdownLabel) {
             dropdownLabel.textContent = window.bundleTranslations.selectPin + ' 1'
          }
        }
      })
    })

    // === DRAG AND DROP FUNCTIONALITY ===
    
    // Handle drag start for pin variant buttons
    root.querySelectorAll<HTMLButtonElement>('[data-pin-draggable]').forEach((button) => {
      // --- TOUCH EVENTS FOR MOBILE ---
      let touchProxy: HTMLImageElement | null = null
      let isTouchDragging = false
      let startX = 0
      let startY = 0
      let isScrolling = false

      // Apply pan-x to allow horizontal scrolling but block vertical gesture starting on the button
      // This helps the browser know that vertical gestures are for the application (drag), not page scroll.
      button.style.touchAction = 'pan-x'

      // When starting a drag from the list, update active pin position if not set
      // This helps with UX so the user knows which pin they might be replacing if they drop
      // although dropping overrides the position anyway.
      
      button.addEventListener('touchstart', (e) => {
        if (button.disabled) return
        isTouchDragging = true
        isScrolling = false
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
      }, { passive: true })

      button.addEventListener('touchmove', (e) => {
        if (!isTouchDragging) return
        if (isScrolling) return

        const touch = e.touches[0]
        
        // Create proxy if it doesn't exist
        if (!touchProxy) {
          const currentX = touch.clientX
          const currentY = touch.clientY
          const deltaX = Math.abs(currentX - startX)
          const deltaY = Math.abs(currentY - startY)

          // Threshold to decide intent
          if (deltaX < 10 && deltaY < 10) return 

          // If moving horizontally more than vertically, assume scroll
          if (deltaX > deltaY) {
            isScrolling = true
            return
          }

          const imageSrc = button.dataset.image
          if (imageSrc) {
            touchProxy = document.createElement('img')
            touchProxy.src = imageSrc
            touchProxy.style.position = 'fixed'
            touchProxy.style.width = '50px'
            touchProxy.style.height = '50px'
            touchProxy.style.pointerEvents = 'none'
            touchProxy.style.zIndex = '9999'
            touchProxy.style.objectFit = 'contain'
            touchProxy.style.transform = 'translate(-50%, -50%)'
            document.body.appendChild(touchProxy)
            
            // Add visual feedback to button
            button.style.opacity = '0.5'
          }
        }
        
        // Update proxy position
        if (touchProxy) {
          if (e.cancelable) e.preventDefault() // Prevent scrolling while dragging
          touchProxy.style.left = `${touch.clientX}px`
          touchProxy.style.top = `${touch.clientY}px`
        } else if (!isScrolling) {
          // If we haven't decided it's scrolling, and we are dragging (vertically mostly),
          // we should prevent default to stop page scroll before proxy is even created
          // if we are sure it's not a horizontal scroll.
          
          const touch = e.touches[0]
          const currentX = touch.clientX
          const currentY = touch.clientY
          const deltaX = Math.abs(currentX - startX)
          const deltaY = Math.abs(currentY - startY)
          
          if (deltaY > deltaX && deltaY > 5) {
             if (e.cancelable) e.preventDefault()
          }
        }
      }, { passive: false }) // passive: false needed for preventDefault

      button.addEventListener('touchend', (e) => {
        if (!isTouchDragging) return
        isTouchDragging = false
        isScrolling = false
        
        // Clean up proxy
        if (touchProxy) {
          touchProxy.remove()
          touchProxy = null
        }
        
        // Remove visual feedback
        button.style.opacity = ''
        
        // Check drop target
        const touch = e.changedTouches[0]
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY)
        const dropZone = elements.find(el => el.hasAttribute('data-pin-drop-zone'))
        
        if (dropZone) {
          const circle = dropZone as SVGCircleElement
          const position = parseInt(circle.dataset.bundlePinImage || circle.dataset.bundlePinCircle || '0', 10)
          const variantId = button.dataset.variantId
          
          if (position && variantId && !isPinBlocked(root, selections, position)) {
            const groupKey = `pin-${position}`
            selections[groupKey] = variantId
            
            const hiddenInput = root.querySelector<HTMLInputElement>(
              `[data-option-group="${groupKey}"]`
            )
            if (hiddenInput) {
              hiddenInput.dataset.selectedId = variantId
            }
            
            updatePreview()
            updateTotal()
            updateSummary()
            updateButtonState()
            updatePinDropdownStatuses()
            hideMessage(errorContainer)
            hideMessage(successContainer)
            //updateOrnamentWarnings(root, selections)
            
            // Temporary highlight for feedback
            highlightDropZone(circle)
            setTimeout(() => removeDropZoneHighlight(circle), 500)
          }
        }
      })

      // --- MOUSE EVENTS (Standard HTML5 Drag & Drop) ---
      button.addEventListener('dragstart', (e) => {
        if (button.disabled || !e.dataTransfer) return
        
        const variantId = button.dataset.variantId
        const price = button.dataset.price
        const image = button.dataset.image
        const label = button.dataset.label
        
        if (!variantId) return
        
        // Store data for drop event
        e.dataTransfer.setData('application/json', JSON.stringify({
          variantId,
          price,
          image,
          label,
          type: 'pin-variant'
        }))
        
        e.dataTransfer.effectAllowed = 'copy'
        
        // Add visual feedback to button
        button.style.opacity = '0.5'
      })
      
      button.addEventListener('dragend', (e) => {
        // Remove visual feedback
        button.style.opacity = ''
      })
    })
    
    // Handle drag events for pin circles (drop zones)
    root.querySelectorAll<SVGCircleElement>('[data-pin-drop-zone]').forEach((circle) => {
      circle.addEventListener('dragover', (e) => {
        e.preventDefault()
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy'
        }
        
        // Highlight the drop zone
        highlightDropZone(circle)
      })
      
      circle.addEventListener('dragleave', (e) => {
        // Remove highlight when leaving
        removeDropZoneHighlight(circle)
      })
      
      circle.addEventListener('drop', (e) => {
        e.preventDefault()
        
        if (!e.dataTransfer) return
        
        try {
          const data = JSON.parse(e.dataTransfer.getData('application/json'))
          
          if (data.type === 'pin-variant') {
            const position = parseInt(circle.dataset.bundlePinImage || circle.dataset.bundlePinCircle || '0', 10)
            if (!position || isPinBlocked(root, selections, position)) return
            
            const groupKey = `pin-${position}`
            selections[groupKey] = data.variantId
            
            const hiddenInput = root.querySelector<HTMLInputElement>(
              `[data-option-group="${groupKey}"]`
            )
            if (hiddenInput) {
              hiddenInput.dataset.selectedId = data.variantId
            }
            
            updatePreview()
            updateTotal()
            updateSummary()
            updateButtonState()
            updatePinDropdownStatuses()
            hideMessage(errorContainer)
            hideMessage(successContainer)
          }
        } catch (error) {
          console.error('Error parsing drag data:', error)
        }
        
        // Remove highlight
        removeDropZoneHighlight(circle)
      })
    })
    
    // Handle drag start for assigned pin images (to support removal)
    root.querySelectorAll<SVGImageElement>('[data-pin-image-draggable]').forEach((image) => {
      // Prevent browser default touch actions (scrolling) on the SVG images entirely
      image.style.touchAction = 'none'

      let draggedPinPosition: number | null = null
      
      // Click handler to select pin position
      image.addEventListener('click', (e) => {
        e.stopPropagation()
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        selectPinPosition(position)
      })
      
      image.addEventListener('dragstart', (e) => {
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (!position || !e.dataTransfer) return
        
        const groupKey = `pin-${position}`
        const selectedId = selections[groupKey]
        
        if (!selectedId) return
        
        draggedPinPosition = position
        
        // Store data for potential removal
        e.dataTransfer.setData('application/json', JSON.stringify({
          position,
          type: 'pin-removal'
        }))
        
        e.dataTransfer.effectAllowed = 'move'
        
        // Add visual feedback
        image.style.opacity = '0.5'
      })
      
      image.addEventListener('dragend', (e) => {
        // Remove visual feedback
        image.style.opacity = ''
      })
      
      // --- TOUCH EVENTS FOR SVG IMAGES (Mobile Support) ---
      let isTouchDraggingSVG = false
      let touchProxySVG: HTMLImageElement | null = null

      image.addEventListener('touchstart', (e) => {
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (!position) return

        isTouchDraggingSVG = true
        
        // Add visual feedback
        image.style.opacity = '0.5'
        image.style.filter = 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
      }, { passive: true })

      image.addEventListener('touchmove', (e) => {
        if (!isTouchDraggingSVG) return
        
        const touch = e.touches[0]
        
        // Create proxy if needed
        if (!touchProxySVG) {
            const href = image.getAttribute('href')
            if (href) {
              touchProxySVG = document.createElement('img')
              touchProxySVG.src = href
              touchProxySVG.style.position = 'fixed'
              touchProxySVG.style.width = '50px'
              touchProxySVG.style.height = '50px'
              touchProxySVG.style.pointerEvents = 'none'
              touchProxySVG.style.zIndex = '9999'
              touchProxySVG.style.objectFit = 'contain'
              touchProxySVG.style.transform = 'translate(-50%, -50%)'
              document.body.appendChild(touchProxySVG)
            }
        }
        
        if (touchProxySVG) {
            e.preventDefault() // Stop scrolling
            touchProxySVG.style.left = `${touch.clientX}px`
            touchProxySVG.style.top = `${touch.clientY}px`
        }
      }, { passive: false })

      image.addEventListener('touchend', (e) => {
        if (!isTouchDraggingSVG) return
        isTouchDraggingSVG = false
        
        // Cleanup
        if (touchProxySVG) {
            touchProxySVG.remove()
            touchProxySVG = null
        }
        
        image.style.opacity = ''
        image.style.filter = ''
        
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (!position) return

        const touch = e.changedTouches[0]
        const elements = document.elementsFromPoint(touch.clientX, touch.clientY)
        
        // Check for drop zone (move pin)
        const dropZone = elements.find(el => el.hasAttribute('data-pin-drop-zone'))
        
        if (dropZone) {
          const targetCircle = dropZone as SVGCircleElement
          const targetPosition = parseInt( targetCircle.dataset.bundlePinImage || targetCircle.dataset.bundlePinCircle || '0', 10)
          
          if (targetPosition && targetPosition !== position && !isPinBlocked(root, selections, targetPosition)) {
            const sourceKey = `pin-${position}`
            const targetKey = `pin-${targetPosition}`
            const pinToMove = selections[sourceKey]
            
            if (pinToMove) {
              const oldPin = selections[targetKey]
              selections[targetKey] = pinToMove
              selections[sourceKey] = oldPin
              
              const sourceInput = root.querySelector<HTMLInputElement>(
                `[data-option-group="${sourceKey}"]`
              )
              const targetInput = root.querySelector<HTMLInputElement>(
                `[data-option-group="${targetKey}"]`
              )
              
              if (sourceInput) sourceInput.dataset.selectedId = oldPin || ''
              if (targetInput) targetInput.dataset.selectedId = pinToMove || ''
              
              updatePreview()
              updateTotal()
              updateSummary()
              updateButtonState()
              updatePinDropdownStatuses()
              hideMessage(errorContainer)
              hideMessage(successContainer)
            }
          }
        } else {
            // Check for removal (drop outside or on pins section)
            const isOutsideBundle = !elements.some(el => el.closest('[data-bundle-root]') === root)
            const isPinSection = elements.some(el => el.closest('[data-pins-section]'))
            
            if (isOutsideBundle || isPinSection) {
                const groupKey = `pin-${position}`
                selections[groupKey] = null
                
                const hiddenInput = root.querySelector<HTMLInputElement>(
                  `[data-option-group="${groupKey}"]`
                )
                if (hiddenInput) {
                  hiddenInput.dataset.selectedId = ''
                }
                
                updatePreview()
                updateTotal()
                updateSummary()
            updateButtonState()
            updatePinDropdownStatuses()
            clearPinCircleHighlight()
            hideMessage(errorContainer)
            hideMessage(successContainer)
            updateOrnamentWarnings(root, selections)
          }
        }
      })

      // Add mousedown handler as fallback for SVG drag issues
      let isDragging = false
      let startX = 0
      let startY = 0
      let dragProxy: HTMLImageElement | null = null
      
      image.addEventListener('mousedown', (e) => {
        // Prevent default browser drag behavior to use our manual implementation
        e.preventDefault()
        
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (!position) return
        
        const groupKey = `pin-${position}`
        const selectedId = selections[groupKey]
        if (!selectedId) return
        
        isDragging = true
        startX = e.clientX
        startY = e.clientY
        
        // Add visual feedback
        image.style.opacity = '0.5'
        image.style.filter = 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
      })
      
      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return
        
        // Check if we've moved enough to consider it a drag (not just a click)
        const deltaX = Math.abs(e.clientX - startX)
        const deltaY = Math.abs(e.clientY - startY)
        
        if (deltaX > 5 || deltaY > 5) {
          // It's a drag, not a click - prevent click from firing
          image.style.pointerEvents = 'none'
          
          // Create drag proxy if it doesn't exist
          if (!dragProxy) {
            const href = image.getAttribute('href')
            if (href) {
              dragProxy = document.createElement('img')
              dragProxy.src = href
              dragProxy.style.position = 'fixed'
              dragProxy.style.width = '50px' // Slightly larger for better visibility
              dragProxy.style.height = '50px'
              dragProxy.style.pointerEvents = 'none' // Important to let events pass through to drop zones
              dragProxy.style.zIndex = '9999'
              dragProxy.style.objectFit = 'contain'
              dragProxy.style.transform = 'translate(-50%, -50%)' // Center on cursor
              document.body.appendChild(dragProxy)
            }
          }
          
          // Update proxy position
          if (dragProxy) {
            dragProxy.style.left = `${e.clientX}px`
            dragProxy.style.top = `${e.clientY}px`
          }
          
          setTimeout(() => {
            image.style.pointerEvents = 'auto'
          }, 100)
        }
      })
      
      document.addEventListener('mouseup', (e) => {
        if (!isDragging) return
        
        isDragging = false
        image.style.opacity = ''
        image.style.filter = ''
        
        // Remove drag proxy
        if (dragProxy) {
          dragProxy.remove()
          dragProxy = null
        }
        
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (!position) return
        
        // Check if mouse is over a drop zone
        const elements = document.elementsFromPoint(e.clientX, e.clientY)
        const dropZone = elements.find(el => el.hasAttribute('data-pin-drop-zone'))
        
        if (dropZone) {
          const targetCircle = dropZone as SVGCircleElement
          const targetPosition = parseInt( targetCircle.dataset.bundlePinImage || targetCircle.dataset.bundlePinCircle || '0', 10)
          
          if (targetPosition && targetPosition !== position && !isPinBlocked(root, selections, targetPosition)) {
            // Move pin to new position
            const sourceKey = `pin-${position}`
            const targetKey = `pin-${targetPosition}`
            const pinToMove = selections[sourceKey]
            
        
            if (pinToMove) {
              selections[sourceKey] = selections[targetKey]
              selections[targetKey] = pinToMove
              
              const sourceInput = root.querySelector<HTMLInputElement>(
                `[data-option-group="${sourceKey}"]`
              )
              const targetInput = root.querySelector<HTMLInputElement>(
                `[data-option-group="${targetKey}"]`
              )
              
              if (sourceInput) sourceInput.dataset.selectedId = selections[sourceKey] || ''
              if (targetInput) targetInput.dataset.selectedId = pinToMove || ''
              
              updatePreview()
              updateTotal()
              updateSummary()
              updateButtonState()
              updatePinDropdownStatuses()
              hideMessage(errorContainer)
              hideMessage(successContainer)
            }
          }
        } else {
          // Check if dropped outside bundle (for removal)
          const isOutsideBundle = !elements.some(el => el.closest('[data-bundle-root]') === root)
          
          // Also allow removal if dropped back onto the pins section
          const isPinSection = elements.some(el => el.closest('[data-pins-section]'))
          
          if (isOutsideBundle || isPinSection) {
            const groupKey = `pin-${position}`
            selections[groupKey] = null
            
            const hiddenInput = root.querySelector<HTMLInputElement>(
              `[data-option-group="${groupKey}"]`
            )
            if (hiddenInput) {
              hiddenInput.dataset.selectedId = ''
            }
            
            updatePreview()
            updateTotal()
            updateSummary()
            updateButtonState()
            updatePinDropdownStatuses()
            clearPinCircleHighlight()
            hideMessage(errorContainer)
            hideMessage(successContainer)
            updateOrnamentWarnings(root, selections)
          }
        }
      })
      
      // Pin images should also act as drop zones (for replacing pins)
      image.addEventListener('dragover', (e) => {
        e.preventDefault()
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy'
        }
        
        // Highlight the associated circle
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (position) {
          const circle = root.querySelector<SVGCircleElement>(
            `[data-bundle-pin-circle="${position}"]`
          )
          if (circle) {
            highlightDropZone(circle)
          }
        }
      })
      
      image.addEventListener('dragleave', (e) => {
        // Remove highlight from associated circle
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (position) {
          const circle = root.querySelector<SVGCircleElement>(
            `[data-bundle-pin-circle="${position}"]`
          )
          if (circle) {
            removeDropZoneHighlight(circle)
          }
        }
      })
      
      image.addEventListener('drop', (e) => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!e.dataTransfer) return
        
        try {
          const data = JSON.parse(e.dataTransfer.getData('application/json'))
          
          if (data.type === 'pin-variant') {
            const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
            if (!position || isPinBlocked(root, selections, position)) return
            
            const groupKey = `pin-${position}`
            selections[groupKey] = data.variantId
            
            const hiddenInput = root.querySelector<HTMLInputElement>(
              `[data-option-group="${groupKey}"]`
            )
            if (hiddenInput) {
              hiddenInput.dataset.selectedId = data.variantId
            }
            
            updatePreview()
            updateTotal()
            updateSummary()
            updateButtonState()
            updatePinDropdownStatuses()
            hideMessage(errorContainer)
            hideMessage(successContainer)
          }
        } catch (error) {
          console.error('Error parsing drag data:', error)
        }
        
        // Remove highlight from associated circle
        const position = parseInt(image.dataset.pinImageDraggable || '0', 10)
        if (position) {
          const circle = root.querySelector<SVGCircleElement>(
            `[data-bundle-pin-circle="${position}"]`
          )
          if (circle) {
            removeDropZoneHighlight(circle)
          }
        }
      })
    })
    
    // Handle document-level drop for pin removal (when dropped outside SVG)
    const svgPreview = root.querySelector<SVGElement>('[data-bundle-preview]')
    if (svgPreview) {
      // Prevent default drop on SVG (except circles) to allow removal detection
      svgPreview.addEventListener('dragover', (e) => {
        const target = e.target as Element
        // Only prevent default if we're over a drop zone (circle)
        if (target.hasAttribute('data-pin-drop-zone')) {
          e.preventDefault()
        }
      })
      
      // Handle drops on the SVG (but not on circles) - this is for removal
      svgPreview.addEventListener('drop', (e) => {
        const target = e.target as Element
        
        // If dropped on a circle, let the circle handler deal with it
        if (target.hasAttribute('data-pin-drop-zone')) {
          return
        }
        
        // Otherwise, this is a removal action
        if (!e.dataTransfer) return
        
        try {
          const data = JSON.parse(e.dataTransfer.getData('application/json'))
          
          if (data.type === 'pin-removal') {
            e.preventDefault()
            e.stopPropagation()
            
            const position = data.position
            const groupKey = `pin-${position}`
            
            selections[groupKey] = null
            
            const hiddenInput = root.querySelector<HTMLInputElement>(
              `[data-option-group="${groupKey}"]`
            )
            if (hiddenInput) {
              hiddenInput.dataset.selectedId = ''
            }
            
            updatePreview()
            updateTotal()
            updateSummary()
            updateButtonState()
            updatePinDropdownStatuses()
            clearPinCircleHighlight()
            hideMessage(errorContainer)
            hideMessage(successContainer)
            updateOrnamentWarnings(root, selections)
          }
        } catch (error) {
          // Ignore errors - likely not our drag data
        }
      })
    }
    
    // Also handle drops outside the entire bundle root for removal
    const handleDocumentDrop = (e: DragEvent) => {
      if (!e.dataTransfer) return
      
      const target = e.target as Element
      
      // Check if the drop target is outside this specific bundle
      const isOutsideBundle = !target.closest('[data-bundle-root]') || target.closest('[data-bundle-root]') !== root
      
      if (!isOutsideBundle) return
      
      try {
        const data = JSON.parse(e.dataTransfer.getData('application/json'))
        
        if (data.type === 'pin-removal') {
          e.preventDefault()
          e.stopPropagation()
          
          const position = data.position
          const groupKey = `pin-${position}`
          
          selections[groupKey] = null
          
          const hiddenInput = root.querySelector<HTMLInputElement>(
            `[data-option-group="${groupKey}"]`
          )
          if (hiddenInput) {
            hiddenInput.dataset.selectedId = ''
          }
          
          updatePreview()
          updateTotal()
          updateSummary()
          updateButtonState()
          updatePinDropdownStatuses()
          clearPinCircleHighlight()
          hideMessage(errorContainer)
          hideMessage(successContainer)
          updateOrnamentWarnings(root, selections)
        }
      } catch (error) {
        // Ignore errors - likely not our drag data
      }
    }
    
    const handleDocumentDragOver = (e: DragEvent) => {
      // Allow drop anywhere for removal
      if (e.dataTransfer?.types.includes('application/json')) {
        e.preventDefault()
      }
    }
    
    // Attach document-level handlers
    document.addEventListener('dragover', handleDocumentDragOver)
    document.addEventListener('drop', handleDocumentDrop)
    
    // Store cleanup function for potential future use
    root.dataset.dragCleanup = 'attached'


    const pdfButton = root.querySelector<HTMLButtonElement>('[data-bundle-download-pdf]')
    if (pdfButton) {
      pdfButton.addEventListener('click', () => handlePdfDownload(root, selections, pdfButton))
    }

    addToCartButton?.addEventListener('click', async () => {
      if (!addToCartButton || isSubmitting) return
      hideMessage(errorContainer)
      hideMessage(successContainer)

      const selectedItems: { id: string, groupKey: string }[] = []
      const seenIds = new Set<string>()

      const blockedPins = getBlockedPins(root, selections)

      for (const groupKey of PREVIEW_ORDER) {
        if (groupKey.startsWith('pin-')) {
          const pinNumber = Number(groupKey.split('-')[1])
          if (blockedPins.has(pinNumber)) {
            continue
          }
        }

        const id = selections[groupKey]
        if (id) {
          // Only de-duplicate non-pin items (soles, straps, size)
          // Pins can be the same variant in multiple positions
          if (groupKey.startsWith('pin-') || !seenIds.has(id)) {
            seenIds.add(id)
            selectedItems.push({ id, groupKey })
          }
        }
      }

      if (!selectedItems.length) {
        showMessage(errorContainer, window.bundleTranslations.nothingToAdd)
        return
      }

      isSubmitting = true
      addToCartButton.disabled = true
      addToCartButton.classList.add('opacity-70', 'cursor-wait')
      addToCartButton.textContent = window.bundleTranslations.adding

      try {
        const bundleId = uuidv4();
        const items = selectedItems.map((item, index) => {
          const numericId = Number(item.id)
          
          let bundlePart = item.groupKey
          if (item.groupKey === 'size') {
            bundlePart = 'main'
          }

          return {
            id: Number.isSafeInteger(numericId) ? numericId : item.id,
            quantity: 1,
            properties: {
              _bundle: bundleId,
              _bundle_main: index === 0 ? 'true' : 'false',
              _bundle_part: index === 0 ? 'main' : bundlePart,
            }
          }
        })

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        })

        if (!response.ok) {
          const payload = await response.json().catch(() => null)
          throw new Error(payload?.description || window.bundleTranslations.unableToAdd)
        }

        showMessage(successContainer, window.bundleTranslations.addedToCart)
        document.dispatchEvent(new CustomEvent('update-cart'))
      } catch (error) {
        console.error(error)
        const message =
          error instanceof Error ? error.message : window.bundleTranslations.genericError
        showMessage(errorContainer, message)
      } finally {
        isSubmitting = false
        addToCartButton.disabled = false
        addToCartButton.classList.remove('opacity-70', 'cursor-wait')
        addToCartButton.textContent = window.bundleTranslations.addToCart
      }
    })

    root.dataset.bundleReady = 'true'
    
    // Wizard functionality for mobile
    function initWizard() {
      const wizardSteps = Array.from(root.querySelectorAll<HTMLElement>('[data-wizard-step]'))
        .sort((a, b) => {
          const stepA = parseInt(a.dataset.wizardStep || '0', 10)
          const stepB = parseInt(b.dataset.wizardStep || '0', 10)
          return stepA - stepB
        })
      
      if (wizardSteps.length === 0) return
      
      let currentStepIndex = 0
      
      const prevButton = root.querySelector<HTMLButtonElement>('[data-wizard-prev]')
      const nextButton = root.querySelector<HTMLButtonElement>('[data-wizard-next]')
      const progressBar = root.querySelector<HTMLElement>('[data-wizard-progress-bar]')
      const stepText = root.querySelector<HTMLElement>('[data-wizard-step-text]')
      const wizardControls = root.querySelector<HTMLElement>('[data-wizard-controls]')
      const sizeGuideButton = root.querySelector<HTMLButtonElement>('[data-size-guide-button]')
      
      if (!prevButton || !nextButton || !progressBar || !stepText || !wizardControls) return
      
      function updateWizardState() {
        // Hide all steps
        wizardSteps.forEach(step => step.classList.remove('wizard-step-active'))
        
        // Show current step
        const currentStep = wizardSteps[currentStepIndex]
        if (currentStep) {
          currentStep.classList.add('wizard-step-active')
          
          // Update step title
          const stepTitle = currentStep.dataset.stepTitle || ''
          const titleElement = wizardControls?.querySelector('p:first-child')
          if (titleElement) {
            titleElement.textContent = stepTitle
          }
          
          // Highlight corresponding SVG part for sole/strap steps
          const optionGroup = currentStep.dataset.optionGroup
          if (optionGroup && (optionGroup.includes('sole') || optionGroup.includes('strap'))) {
            highlightSvgPart(optionGroup)
          } else {
            // Clear highlights for non-sole/strap steps
            clearSvgPartHighlights()
          }
          
          // Auto-select first pin when pins step is reached
          if (currentStep.hasAttribute('data-pins-section') && activePinPosition === null) {
            selectPinPosition(1)
          }
        }

        
        if (currentStep.getAttribute('data-option-group') === 'size') {
          sizeGuideButton?.setAttribute('aria-current', 'true')
          console.log('size', sizeGuideButton)
        } else {
          sizeGuideButton?.removeAttribute('aria-current')
        }
        
        // Update step counter
        if (stepText) {
          stepText.textContent = window.bundleTranslations.stepCounter
            .replace('{{ step }}', String(currentStepIndex + 1))
            .replace('{{ total }}', String(wizardSteps.length))
        }
        
        // Update progress bar
        const progress = ((currentStepIndex + 1) / wizardSteps.length) * 100
        if (progressBar) {
          progressBar.style.width = `${progress}%`
        }
        
        // Update Previous button
        if (prevButton) {
          prevButton.disabled = currentStepIndex === 0
        }
        
        // Update Next button text
        const nextButtonSpan = nextButton?.querySelector('span')
        if (currentStepIndex === wizardSteps.length - 1) {
          root.setAttribute('aria-expanded', 'true')
          // Last step - show "Add to Cart"
          if (nextButtonSpan) {
            nextButtonSpan.innerHTML = window.bundleTranslations.addToCart
          }
        } else {
          root.setAttribute('aria-expanded', 'false')
          // Not last step - show "Next"
          if (nextButtonSpan) {
            nextButtonSpan.innerHTML = `Next<svg class='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M9 5l7 7-7 7'></path></svg>`
          }
        }
        
        // Scroll to top of bundle on mobile
        if (window.innerWidth < 1024) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
      
      function validateCurrentStep(): boolean {
        const currentStep = wizardSteps[currentStepIndex]
        if (!currentStep) return true
        
        const optionGroup = currentStep.dataset.optionGroup
        
        // Summary step doesn't need validation
        if (currentStepIndex === wizardSteps.length - 1) {
          return true
        }
        
        // Pins section is optional
        if (currentStep.hasAttribute('data-pins-section')) {
          return true
        }
        
        // Ornaments are optional
        if (optionGroup === 'left-ornament' || optionGroup === 'right-ornament') {
          return true
        }
        
        // Check if this step has a selection requirement
        if (optionGroup) {
          const selectedId = selections[optionGroup]
          if (!selectedId) {
            showMessage(errorContainer, window.bundleTranslations.selectOptionError.replace('{{ step }}', currentStep.dataset.stepTitle || 'this step'))
            return false
          }
        }
        
        return true
      }
      
      prevButton.addEventListener('click', () => {
        if (currentStepIndex > 0) {
          currentStepIndex--
          updateWizardState()
          hideMessage(errorContainer)
          hideMessage(successContainer)
        }
      })
      
      nextButton.addEventListener('click', () => {
        // If on last step, trigger add to cart
        if (currentStepIndex === wizardSteps.length - 1) {
          addToCartButton?.click()
          return
        }
        
        // Validate current step before advancing
        if (!validateCurrentStep()) {
          return
        }
        
        if (currentStepIndex < wizardSteps.length - 1) {
          currentStepIndex++
          updateWizardState()
          hideMessage(errorContainer)
          hideMessage(successContainer)
        }
      })
      
      // Expose the navigation function for use by scrollToSection
      navigateToWizardStep = (index: number) => {
        if (index >= 0 && index < wizardSteps.length) {
          currentStepIndex = index
          updateWizardState()
          hideMessage(errorContainer)
          hideMessage(successContainer)
        }
      }
      
      // Initialize wizard state
      updateWizardState()
    }

    function updatePreview() {
      // Update sole images in SVG patterns
      const leftSoleImage = root.querySelector<SVGImageElement>('[data-bundle-preview-left-sole]')
      const rightSoleImage = root.querySelector<SVGImageElement>('[data-bundle-preview-right-sole]')
      const leftStrapImage = root.querySelector<SVGImageElement>('[data-bundle-preview-left-strap]')
      const rightStrapImage = root.querySelector<SVGImageElement>('[data-bundle-preview-right-strap]')

      if (leftSoleImage) {
        const selectedId = selections['left-sole']
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="left-sole"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          if (imageUrl) {
            leftSoleImage.setAttribute('href', imageUrl)
          }
        }
      }

      if (rightSoleImage) {
        const selectedId = selections['right-sole']
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="right-sole"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          if (imageUrl) {
            rightSoleImage.setAttribute('href', imageUrl)
          }
        }
      }

      if (leftStrapImage) {
        const selectedId = selections['left-strap']
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="left-strap"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          if (imageUrl) {
            leftStrapImage.setAttribute('href', imageUrl)
          }
        }
      }

      if (rightStrapImage) {
        const selectedId = selections['right-strap']
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="right-strap"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          if (imageUrl) {
            rightStrapImage.setAttribute('href', imageUrl)
          }
        }
      }

      // Update ornament images
      const leftOrnamentImage = root.querySelector<SVGImageElement>('[data-bundle-preview-left-ornament]')
      const rightOrnamentImage = root.querySelector<SVGImageElement>('[data-bundle-preview-right-ornament]')

      if (leftOrnamentImage) {
        const selectedId = selections['left-ornament']
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="left-ornament"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          const config = button?.dataset.config || '{}'
          
          const configJson = JSON.parse(config) as { image: { width: string, height: string, x: string, y: string, style: string } }

          const style = `x: ${configJson.image.x}; y: ${configJson.image.y}; width: ${configJson.image.width}; height: ${configJson.image.height}; ` + (configJson.image.style || '')

          if (imageUrl && configJson.image) {
            leftOrnamentImage.setAttribute('href', imageUrl)
            leftOrnamentImage.setAttribute('style', style);
            leftOrnamentImage.style.display = 'block'
            leftOrnamentImage.setAttribute('width', configJson.image.width)
            leftOrnamentImage.setAttribute('height', configJson.image.height)
            leftOrnamentImage.setAttribute('x', configJson.image.x)
            leftOrnamentImage.setAttribute('y', configJson.image.y)
            
          } else {
            leftOrnamentImage.style.display = 'none'
          }
        } else {
          leftOrnamentImage.style.display = 'none'
        }
      }

      if (rightOrnamentImage) {
        const selectedId = selections['right-ornament']
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="right-ornament"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          const config = button?.dataset.config || '{}'
          
          const configJson = JSON.parse(config) as { image: { width: string, height: string, x: string, y: string, style: string } }

          const style = `x: ${configJson.image.x}; y: ${configJson.image.y}; width: ${configJson.image.width}; height: ${configJson.image.height}; ` + (configJson.image.style || '')

          if (imageUrl && configJson.image) {
            rightOrnamentImage.setAttribute('href', imageUrl)
            rightOrnamentImage.setAttribute('style', style);
            rightOrnamentImage.style.display = 'block'
            rightOrnamentImage.setAttribute('width', configJson.image.width)
            rightOrnamentImage.setAttribute('height', configJson.image.height)
            rightOrnamentImage.setAttribute('x', configJson.image.x)
            rightOrnamentImage.setAttribute('y', configJson.image.y)
          } else {
            rightOrnamentImage.style.display = 'none'
          }
        } else {
          rightOrnamentImage.style.display = 'none'
        }
      }

      // Update pin images
      for (let i = 1; i <= 8; i++) {
        const groupKey = `pin-${i}`
        const selectedId = selections[groupKey]
        const pinImage = root.querySelector<SVGImageElement>(`[data-bundle-pin-image="${i}"]`)
        
        if (!pinImage) continue

        if (isPinBlocked(root, selections, i)) {
          pinImage.style.display = 'none'
          continue
        }
        
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-bundle-pin-variant][data-variant-id="${selectedId}"]`
          )
          const imageUrl = button?.dataset.image
          if (imageUrl) {
            pinImage.setAttribute('href', imageUrl)
            pinImage.style.display = 'block'
            pinImage.style.pointerEvents = 'auto'
            pinImage.style.cursor = 'move'
            // Ensure draggable is set (SVG elements sometimes need this explicitly)
            pinImage.setAttribute('draggable', 'true')
          } else {
            pinImage.style.display = 'none'
          }
        } else {
          pinImage.style.display = 'none'
          pinImage.setAttribute('href', '')
        }
      }
    }

    function updateTotal() {
      if (!totalDisplay) return
      let total = 0
    
      PREVIEW_ORDER.forEach((groupKey) => {
        const selectedId = selections[groupKey]
        if (!selectedId) return
    
        let button: HTMLButtonElement | null = null
    
        if (groupKey.startsWith('pin-')) {
          button = root.querySelector<HTMLButtonElement>(
            `[data-bundle-pin-variant][data-variant-id="${selectedId}"]`
          )
        } else {
          button = root.querySelector<HTMLButtonElement>(
            `[data-option-group="${groupKey}"] [data-bundle-option][data-variant-id="${selectedId}"]`
          )
        }
    
        const price = button?.dataset.price
        if (price) total += Number(price)
      })
    
      totalDisplay.textContent = formatter(total)
      
      // Also update all elements with data-bundle-total
      root.querySelectorAll<HTMLElement>('[data-bundle-total]').forEach((el) => {
        el.textContent = formatter(total)
      })
    }

    function updateSummary() {
      const blockedPins = getBlockedPins(root, selections)
      const pinPositionNames = Array.from({ length: 8 }, (_, i) => {
        const num = i + 1;
        const key = num < 5 ? 'leftPin' : 'rightPin';
        const pinNum = num < 5 ? num : num - 4;
        return window.bundleTranslations[key].replace('{{ number }}', String(pinNum));
      });
      
      // Update each summary item
      PREVIEW_ORDER.forEach((groupKey, index) => {
        const summaryRow = root.querySelector<HTMLElement>(`[data-summary-item="${groupKey}"]`)
        if (!summaryRow) return
        
        const selectedId = selections[groupKey]
        const labelEl = summaryRow.querySelector<HTMLElement>('[data-summary-label]')
        const priceEl = summaryRow.querySelector<HTMLElement>('[data-summary-price]')
        
        if (!labelEl || !priceEl) return
        
        if (groupKey.startsWith('pin-')) {
          const pinNumber = Number(groupKey.split('-')[1])
          if (blockedPins.has(pinNumber)) {
            summaryRow.classList.add('hidden')
            return
          }
        }

        if (selectedId) {
          // Find the button for this selection
          let button: HTMLButtonElement | null = null
          
          if (groupKey.startsWith('pin-')) {
            button = root.querySelector<HTMLButtonElement>(
              `[data-bundle-pin-variant][data-variant-id="${selectedId}"]`
            )
          } else {
            button = root.querySelector<HTMLButtonElement>(
              `[data-option-group="${groupKey}"] [data-bundle-option][data-variant-id="${selectedId}"]`
            )
          }
          
          if (button) {
            const label = button.dataset.label || ''
            const price = button.dataset.price || '0'
            
            // Set the label based on the group type
            if (groupKey === 'size') {
              labelEl.textContent = `${window.bundleTranslations.size}: ${label}`
            } else if (groupKey === 'left-sole') {
              labelEl.textContent = `${window.bundleTranslations.leftOutsole}: ${label}`
            } else if (groupKey === 'right-sole') {
              labelEl.textContent = `${window.bundleTranslations.rightOutsole}: ${label}`
            } else if (groupKey === 'left-strap') {
              labelEl.textContent = `${window.bundleTranslations.leftStrap}: ${label}`
            } else if (groupKey === 'right-strap') {
              labelEl.textContent = `${window.bundleTranslations.rightStrap}: ${label}`
            } else if (groupKey.startsWith('pin-')) {
              const pinNumber = parseInt(groupKey.split('-')[1])
              labelEl.textContent = `${pinPositionNames[pinNumber - 1]}`
            } else if (groupKey === 'left-ornament') {
              labelEl.textContent = window.bundleTranslations.leftOrnaments
            } else if (groupKey === 'right-ornament') {
              labelEl.textContent = window.bundleTranslations.rightOrnaments
            }
            
            priceEl.textContent = formatter(Number(price))
            summaryRow.classList.remove('hidden')
          } else {
            summaryRow.classList.add('hidden')
          }
        } else {
          summaryRow.classList.add('hidden')
        }
      })
    }

    function updateButtonState() {
      return;
      /*
      if (!addToCartButton) return
      const allGroups = Array.from(root.querySelectorAll<HTMLElement>('[data-option-group]'))
        .map((section) => section.dataset.optionGroup)
        .filter(Boolean) as string[]
        console.log(allGroups)
      const canSubmit = allGroups.every((groupKey) => Boolean(selections[groupKey]))
      addToCartButton.disabled = !canSubmit || isSubmitting
      addToCartButton.classList.toggle('opacity-70', addToCartButton.disabled)
      addToCartButton.classList.toggle('cursor-not-allowed', addToCartButton.disabled)
      */
    }

    function updateDropdownLabel(position: number) {
      const dropdownLabel = root.querySelector<HTMLElement>('[data-bundle-pin-dropdown-label]')
      if (dropdownLabel) {
        const groupKey = `pin-${position}`
        const selectedId = selections[groupKey]
        
        if (selectedId) {
          const button = root.querySelector<HTMLButtonElement>(
            `[data-bundle-pin-variant][data-variant-id="${selectedId}"]`
          )
          const label = button?.dataset.label || window.bundleTranslations.selected
          dropdownLabel.textContent = `${window.bundleTranslations.pin} ${position}: ${label}`
        } else {
          dropdownLabel.textContent = `${window.bundleTranslations.selectPin} ${position}`
        }
      }
    }

    function updatePinDropdownStatuses() {
      for (let i = 1; i <= 8; i++) {
        const groupKey = `pin-${i}`
        const selectedId = selections[groupKey]
        const statusLabel = root.querySelector<HTMLElement>(`[data-pin-dropdown-status="${i}"]`)
        
        if (!statusLabel) continue
        
        if (selectedId) {
          console.log(selectedId, statusLabel)
          const button = root.querySelector<HTMLButtonElement>(
            `[data-bundle-pin-variant][data-variant-id="${selectedId}"]`
          )
          statusLabel.textContent = button?.dataset.label || window.bundleTranslations.selected
          statusLabel.classList.remove('text-neutral-500')
          statusLabel.classList.add('text-primary')
        } else {
          statusLabel.textContent = window.bundleTranslations.none
          statusLabel.classList.remove('text-primary')
          statusLabel.classList.add('text-neutral-500')
        }
      }
      
      // Also update the main dropdown label
      if (activePinPosition) {
        updateDropdownLabel(activePinPosition)
      }
    }

    function highlightPinCircle(position: number) {
      // Clear all highlights first
      root.querySelectorAll<SVGCircleElement>('[data-bundle-pin-circle]').forEach((circle) => {
        circle.style.fill = 'white'
        circle.setAttribute('r', '3')
      })
      
      // Highlight the selected circle
      const circle = root.querySelector<SVGCircleElement>(
        `[data-bundle-pin-circle="${position}"]`
      )
      if (circle) {
        circle.style.fill = '#11204C'
        circle.setAttribute('r', '5')
      }
      
      // Also highlight the pin image if it exists
      highlightActivePinImage()
    }

    function clearPinCircleHighlight() {
      root.querySelectorAll<SVGCircleElement>('[data-bundle-pin-circle]').forEach((circle) => {
        circle.style.fill = 'white'
        circle.setAttribute('r', '3')
      })
      clearActivePinImageHighlight()
    }
    
    function highlightActivePinImage() {
      // Clear all pin image highlights first
      clearActivePinImageHighlight()
      
      if (activePinPosition === null) return
      
      // Highlight the active pin's circle
      const circle = root.querySelector<SVGCircleElement>(
        `[data-bundle-pin-circle="${activePinPosition}"]`
      )
      if (circle) {
        circle.style.fill = '#11204C'
        circle.setAttribute('r', '5')
      }
      
      // Highlight the active pin image if it exists
      const pinImage = root.querySelector<SVGImageElement>(
        `[data-bundle-pin-image="${activePinPosition}"]`
      )
      if (pinImage && pinImage.style.display === 'block') {
        // Get current position and size
        const x = parseFloat(pinImage.getAttribute('x') || '0')
        const y = parseFloat(pinImage.getAttribute('y') || '0')
        const width = parseFloat(pinImage.getAttribute('width') || '0')
        const height = parseFloat(pinImage.getAttribute('height') || '0')
        
        // Calculate center point
        const centerX = x + width / 2
        const centerY = y + height / 2
        
        // Add a subtle glow and scale effect from the center
        pinImage.style.filter = 'drop-shadow(0 0 6px rgba(17, 32, 76, 0.8)) drop-shadow(0 0 12px rgba(17, 32, 76, 0.4))'
        pinImage.style.transformOrigin = `${centerX}px ${centerY}px`
        pinImage.style.transform = 'scale(1.1)'
        pinImage.style.transition = 'all 0.2s ease'
      }
      
      // Highlight the corresponding pin button in the pins box
      highlightActivePinButton()
    }
    
    function clearActivePinImageHighlight() {
      // Remove highlights from all pin images
      root.querySelectorAll<SVGImageElement>('[data-bundle-pin-image]').forEach((img) => {
        img.style.filter = ''
        img.style.transform = ''
        img.style.transformOrigin = ''
        img.style.transition = ''
      })
      
      // Clear pin button highlights
      clearActivePinButtonHighlight()
    }
    
    function highlightActivePinButton() {
      // Clear all pin button highlights first
      clearActivePinButtonHighlight()
      
      if (activePinPosition === null) return
      
      // Get the selected pin variant for this position
      const groupKey = `pin-${activePinPosition}`
      const selectedId = selections[groupKey]
      
      if (!selectedId) return
      
      // Find and highlight the corresponding button in the pins section
      const pinButton = root.querySelector<HTMLButtonElement>(
        `[data-bundle-pin-variant][data-variant-id="${selectedId}"]`
      )
      
      if (pinButton) {
        // Add highlight styles
        pinButton.setAttribute('aria-selected', 'true')
      }
    }
    
    function clearActivePinButtonHighlight() {
      // Remove highlights from all pin variant buttons
      root.querySelectorAll<HTMLButtonElement>('[data-bundle-pin-variant]').forEach((btn) => {
        btn.removeAttribute('aria-selected')
      })
    }
    
    function highlightDropZone(circle: SVGCircleElement) {
      // Store original values if not already stored
      if (!circle.dataset.originalFill) {
        circle.dataset.originalFill = circle.style.fill || circle.getAttribute('fill') || 'white'
      }
      if (!circle.dataset.originalRadius) {
        circle.dataset.originalRadius = circle.getAttribute('r') || '3'
      }
      
      // Apply highlight
      circle.style.fill = '#11204C'
      circle.setAttribute('r', '6')
      circle.style.transition = 'all 0.2s ease'
    }
    
    function removeDropZoneHighlight(circle: SVGCircleElement) {
      // Restore original values
      const originalFill = circle.dataset.originalFill || 'white'
      const originalRadius = circle.dataset.originalRadius || '3'
      
      circle.style.fill = originalFill
      circle.setAttribute('r', originalRadius)
    }

    // SVG Part Highlighting Functions
    function highlightSvgPart(partName: string) {
      // Clear existing highlights first
      clearSvgPartHighlights()
      
      // Find the SVG rect for this part
      const svgPart = root.querySelector<SVGRectElement>(`[data-bundle-part="${partName}"]`)
      if (!svgPart) return
      
      // Apply highlight effect
      svgPart.setAttribute('aria-selected', 'true')
    }

    function clearSvgPartHighlights() {
      // Remove highlights from all SVG parts
      root.querySelectorAll<SVGRectElement>('[data-bundle-part]').forEach((part) => {
        part.removeAttribute('aria-selected')
      })
    }

    function getSectionForPart(partName: string): HTMLElement | null {
      // Map part names to their corresponding section data-option-group values
      return root.querySelector<HTMLElement>(`[data-option-group="${partName}"]`)
    }

    // Store wizard navigation function reference

    function isPinBlocked(root: HTMLElement, selections: SelectionState, pinNumber: number): boolean {
      const blockedPins = getBlockedPins(root, selections)
      return blockedPins.has(pinNumber)
    }

    function getBlockedPins(root: HTMLElement, selections: SelectionState): Set<number> {
      const blockedPins = new Set<number>()
      
      ;['left-ornament', 'right-ornament'].forEach(groupKey => {
        const selectedId = selections[groupKey]
        if (!selectedId) return
        
        const button = root.querySelector<HTMLElement>(
          `[data-bundle-option="${groupKey}"][value="${selectedId}"]`
        )
        if (!button) return
        
        try {
          const jsonData = JSON.parse(button.dataset.config || '{}')
          if (jsonData.blocked_pins) {
            if (Array.isArray(jsonData.blocked_pins)) {
              jsonData.blocked_pins.forEach(p => blockedPins.add(Number(p)))
            }
          }
        } catch (e) {
          // ignore
        }
      })
      
      return blockedPins
    }

    function updatePinAvailability(root: HTMLElement, selections: SelectionState) {
      const blockedPins = getBlockedPins(root, selections)
      
      // Update pin circles and dropdown items
      for (let i = 1; i <= 8; i++) {
        const isBlocked = blockedPins.has(i)
        
        // Circles
        const circle = root.querySelector<SVGCircleElement>(`[data-bundle-pin-circle="${i}"]`)
        if (circle) {
          if (isBlocked) {
            circle.classList.add('pointer-events-none', 'opacity-20', 'cursor-not-allowed')
            circle.classList.remove('cursor-pointer', 'hover:fill-primary')
            circle.setAttribute('aria-disabled', 'true')
          } else {
            circle.classList.remove('pointer-events-none', 'opacity-20', 'cursor-not-allowed')
            circle.classList.add('cursor-pointer', 'hover:fill-primary')
            circle.setAttribute('aria-disabled', 'false')
          }
        }
        
        // Dropdown items
        const dropdownItem = root.querySelector<HTMLButtonElement>(`[data-bundle-pin-position-select="${i}"]`)
        if (dropdownItem) {
          dropdownItem.disabled = isBlocked
          if (isBlocked) {
            dropdownItem.classList.add('opacity-50', 'cursor-not-allowed')
            const status = dropdownItem.querySelector('[data-pin-dropdown-status]')
            if (status) status.textContent = 'Unavailable' // Or translated string
          } else {
            dropdownItem.classList.remove('opacity-50', 'cursor-not-allowed')
          }
        }
      }
      
      // If active pin is blocked, deselect it
      if (activePinPosition && blockedPins.has(activePinPosition)) {
        activePinPosition = null
        if (pinDropdownToggle) {
           const icon = pinDropdownToggle.querySelector<SVGElement>('[data-bundle-pin-dropdown-icon]')
           if (icon) icon.style.transform = 'rotate(0deg)'
        }
        const pinDropdownMenu = root.querySelector<HTMLElement>('[data-bundle-pin-dropdown-menu]')
        if (pinDropdownMenu) pinDropdownMenu.classList.add('hidden')
        
        // Just reset the dropdown label text directly instead of calling updateDropdownLabel with invalid index
        const dropdownLabel = root.querySelector<HTMLElement>('[data-bundle-pin-dropdown-label]')
        if (dropdownLabel) {
           dropdownLabel.textContent = window.bundleTranslations.selectPin + ' 1'
        }
        
        clearPinCircleHighlight()
      }
    }

    function scrollToSection(partName: string) {
      const section = getSectionForPart(partName)
      if (!section) return
      
      // Check if we're on mobile or desktop
      const isMobile = window.innerWidth < 1024
      
      if (isMobile) {
        // On mobile, navigate to the correct wizard step
        const stepNumber = section.dataset.wizardStep
        if (stepNumber) {
          navigateToWizardStep(parseInt(stepNumber, 10) - 1)
        }
      } else {
        // On desktop, smooth scroll to the section
        section.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    function preloadImages(root: HTMLElement) {
      const urls = new Set<string>()
      
      const collect = (selector: string) => {
        root.querySelectorAll<HTMLElement>(selector).forEach(el => {
          if (el.dataset.image) urls.add(el.dataset.image)
        })
      }
      
      collect('[data-bundle-option]')
      collect('[data-bundle-pin-variant]')
      
      if (urls.size === 0) return

      console.log(`[Bundle] Preloading ${urls.size} images...`)
      
      for (const url of urls) {
        const img = new Image()
        img.src = url
      }
    }

    async function handlePdfDownload(root: HTMLElement, selections: SelectionState, button: HTMLButtonElement) {
      if (button.disabled) return
      
      const originalText = button.querySelector('[data-bundle-download-pdf-text]')?.textContent
      const textSpan = button.querySelector('[data-bundle-download-pdf-text]')
      
      try {
        button.disabled = true
        button.classList.add('cursor-wait')
        if (textSpan) textSpan.textContent = window.bundleTranslations.generatingPdf

        console.log('selections', selections)
        
        // 1. Generate Config Link
        const state = JSON.stringify(selections)
        const encoded = btoa(state)
        const url = new URL(window.location.href)
        url.searchParams.set('config', encoded)
        const shareableLink = url.toString()
        
        // 2. Capture Preview
        const svgElement = root.querySelector<SVGSVGElement>('[data-bundle-preview]')
        if (!svgElement) throw new Error('Preview element not found')
        
        const canvas = document.createElement('canvas')
        const scale = 3
        const viewBox = svgElement.viewBox.baseVal
        canvas.width = viewBox.width * scale
        canvas.height = viewBox.height * scale
        
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas context not available')
        
        ctx.scale(scale, scale)
        ctx.translate(-viewBox.x, -viewBox.y)
        
        // Fill background
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(viewBox.x, viewBox.y, viewBox.width, viewBox.height)
        
        const children = Array.from(svgElement.children) as SVGElement[]
        
        for (const child of children) {
          const style = window.getComputedStyle(child)
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue
          
          if (child.tagName.toLowerCase() === 'image') {
             const imgEl = child as SVGImageElement
             const href = imgEl.getAttribute('href') || imgEl.getAttribute('xlink:href')
             if (!href) continue
             
             const x = parseFloat(imgEl.getAttribute('x') || '0')
             const y = parseFloat(imgEl.getAttribute('y') || '0')
             const w = parseFloat(imgEl.getAttribute('width') || '0')
             const h = parseFloat(imgEl.getAttribute('height') || '0')
             
             try {
                const img = new Image()
                img.crossOrigin = 'anonymous'
                await new Promise<void>((resolve) => {
                    img.onload = () => resolve()
                    img.onerror = () => resolve()
                    img.src = href
                })
                ctx.drawImage(img, x, y, w, h)
             } catch (e) {
                console.warn('Failed to draw image', href)
             }
          }
        }
        
        const imgData = canvas.toDataURL('image/png')
        
        // 3. Generate PDF
        if (!window.jspdf || !window.jspdf.jsPDF) throw new Error('jspdf not loaded');
        
        const { jsPDF } = window.jspdf;
        
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        })

        // Load custom font for Latin Extended support (Romanian)
        try {
          const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf'
          const response = await fetch(fontUrl)
          const buffer = await response.arrayBuffer()
          
          // Convert ArrayBuffer to binary string
          let binary = ''
          const bytes = new Uint8Array(buffer)
          const len = bytes.byteLength
          for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i])
          }
          
          // Add font to VFS and register
          pdf.addFileToVFS('Roboto-Regular.ttf', binary)
          pdf.addFont('Roboto-Regular.ttf', 'Roboto', 'normal')
          pdf.setFont('Roboto')
        } catch (e) {
          console.warn('Failed to load custom font, falling back to default', e)
        }
        
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const margin = 20
        
        // Title
        pdf.setFontSize(24)
        pdf.text(window.bundleTranslations.pdfTitle, margin, margin)
        
        // Image (Centered)
        const imgProps = pdf.getImageProperties(imgData)
        const imgWidth = pageWidth - (margin * 2)
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width
        
        pdf.addImage(imgData, 'PNG', margin, margin + 10, imgWidth, imgHeight)
        
        // Summary
        let yPos = margin + 10 + imgHeight + 10
        
        if (yPos > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
        }

        pdf.setFontSize(16)
        pdf.text(window.bundleTranslations.summary, margin, yPos)
        yPos += 10
        
        pdf.setFontSize(12)
        
        const summaryRows = root.querySelectorAll('tbody[data-bundle-summary-items] tr:not(.hidden)')
        
        summaryRows.forEach(row => {
          const label = row.querySelector('[data-summary-label]')?.textContent
          const price = row.querySelector('[data-summary-price]')?.textContent
          
          if (row.querySelector('td[colspan="3"]')) {
             const headerText = row.querySelector('td')?.textContent?.trim()
             if (headerText) {
               yPos += 5
               if (yPos > pageHeight - margin) {
                   pdf.addPage();
                   yPos = margin;
               }
               pdf.setFont('Roboto', 'bold')
               pdf.text(headerText, margin, yPos)
               pdf.setFont('Roboto', 'normal')
               yPos += 7
             }
             return
          }
          
          if (label && price) {
            if (yPos > pageHeight - margin) {
                pdf.addPage();
                yPos = margin;
            }
            pdf.text(label.trim(), margin, yPos)
            pdf.text(price.trim(), pageWidth - margin, yPos, { align: 'right' })
            yPos += 7
          }
        })
        
        // Total
        yPos += 5
        const totalLabel = root.querySelector('tr:last-child td:first-child')?.textContent?.trim()
        const totalPrice = root.querySelector('[data-bundle-total]')?.textContent?.trim()
        
        if (totalLabel && totalPrice) {
          if (yPos > pageHeight - margin) {
              pdf.addPage();
              yPos = margin;
          }
          pdf.setFont('Roboto', 'bold')
          pdf.text(totalLabel, margin, yPos)
          pdf.text(totalPrice, pageWidth - margin, yPos, { align: 'right' })
        }
        
        // Link
        yPos += 20
        if (yPos > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
        }
        pdf.setFontSize(10)
        pdf.setTextColor(100)
        pdf.text(window.bundleTranslations.configurationLink, margin, yPos)
        yPos += 5
        pdf.setTextColor(0, 0, 255)
        
        const splitLink = pdf.splitTextToSize(shareableLink, pageWidth - (margin * 2))
        pdf.text(splitLink, margin, yPos)
        pdf.link(margin, yPos - 5, pageWidth - (margin * 2), 10 * splitLink.length, { url: shareableLink })
        
        pdf.save('shop-waves-flip-flops-offer.pdf')
        
      } catch (error) {
        console.error('PDF Generation failed', error)
        alert('Failed to generate PDF. Please try again.')
      } finally {
        button.disabled = false
        button.classList.remove('cursor-wait')
        if (textSpan && originalText) textSpan.textContent = originalText
      }
    }
  }

  function updateAllSections(root: HTMLElement, selections: SelectionState) {
    root.querySelectorAll<HTMLElement>('[data-option-group]').forEach((section) => {
      const groupKey = section.dataset.optionGroup
      if (!groupKey) return
      updateSectionState(section, groupKey, selections)
    })
  }

  function updateSectionState(section: HTMLElement, groupKey: string, selections: SelectionState) {
    const selectedId = selections[groupKey]
    section.dataset.selectedId = selectedId ?? ''

    const label = section.querySelector<HTMLElement>('[data-bundle-selected-label]')
    const dropdownLabel = section.querySelector<HTMLElement>('[data-bundle-dropdown-label]')
    
    if (label || dropdownLabel) {
      const selectedButton = section.querySelector<HTMLElement>(
        `[data-bundle-option][value="${selectedId}"]`,
      )
      const labelText = selectedButton?.dataset.label ?? window.bundleTranslations.select
      if (label) {
        label.textContent = labelText
      }
      if (dropdownLabel) {
        dropdownLabel.textContent = labelText
      }
      
      // Update the swatch image in the header
      const headerSwatch = section.querySelector<HTMLElement>('header [data-bundle-swatch]')
      
      if (headerSwatch && selectedButton) {
        const image = selectedButton.parentElement?.querySelector<HTMLImageElement>('img');
        const img = headerSwatch.querySelector<HTMLImageElement>('img')

        if (image && img) {
          img.src = image.src
          img.srcset = image.srcset
          img.alt = labelText || 'Selected option'
        }
      }
    }

    section.querySelectorAll<HTMLButtonElement>('[data-bundle-option]').forEach((button) => {
      const isSelected = button.dataset.variantId === selectedId
      button.setAttribute('aria-pressed', String(isSelected))

      if (groupKey === 'size') {
        button.classList.remove('text-neutral-500')
        button.classList.toggle('border-primary', isSelected)
        button.classList.toggle('bg-primary', isSelected)
        button.classList.toggle('text-white', isSelected)
        button.classList.toggle('shadow-md', isSelected)

        button.classList.toggle('border-neutral-200', !isSelected)
        button.classList.toggle('bg-white', !isSelected)
        button.classList.toggle('text-primary', !isSelected)
      } else if (groupKey === 'left-ornament' || groupKey === 'right-ornament') {
        button.classList.toggle('text-primary', isSelected)
        button.classList.toggle('text-neutral-500', !isSelected)

        const iconContainer = button.querySelector<HTMLElement>('div')
        if (iconContainer) {
          iconContainer.classList.toggle('border-primary', isSelected)
          iconContainer.classList.toggle('shadow-lg', isSelected)
          iconContainer.classList.toggle('border-neutral-200', !isSelected)
          iconContainer.classList.toggle('shadow-sm', !isSelected)
        }
      } else {
        button.classList.toggle('text-primary', isSelected)
        button.classList.toggle('text-neutral-500', !isSelected)

        const swatch = button.querySelector<HTMLElement>('[data-bundle-swatch]')
        if (swatch) {
          swatch.classList.toggle('border-primary', isSelected)
          swatch.classList.toggle('shadow-lg', isSelected)
          swatch.classList.toggle('border-transparent', !isSelected)
          swatch.classList.toggle('shadow-sm', !isSelected)
        }
      }
    })
  }

  function styleSwatches(root: HTMLElement) {
    root.querySelectorAll<HTMLElement>('[data-bundle-swatch]').forEach((element) => {
      // Skip if this swatch contains an image (used in headers)
      if (element.querySelector('img')) {
        return
      }
      
      const key = element.dataset.bundleSwatch?.trim().toLowerCase() ?? ''
      const color = COLOR_STYLES[key] || FALLBACK_SWATCH
      if (color.startsWith('linear-gradient') || color.startsWith('radial-gradient')) {
        element.style.backgroundImage = color
        element.style.backgroundColor = 'transparent'
      } else {
        element.style.backgroundColor = color
        element.style.backgroundImage = 'none'
      }
    })
  }

  function createPriceFormatter(currency: string) {
    try {
      const formatter = new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency })
      return (value: number) => formatter.format(value / 100)
    } catch (error) {
      console.warn('Falling back to basic currency formatting', error)
      return (value: number) => `${(value / 100).toFixed(2)} ${currency}`
    }
  }

  function showMessage(container: HTMLElement | null | undefined, message: string) {
    if (!container) return
    container.textContent = message
    container.classList.remove('hidden')
  }

  function hideMessage(container: HTMLElement | null | undefined) {
    if (!container) return
    container.textContent = ''
    container.classList.add('hidden')
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundles)
  } else {
    initBundles()
  }
})()